import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

const OrdenesTrabajo = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [hseChecklist, setHseChecklist] = useState({
    permisoTrabajo: false,
    ats: false,
    epp: false,
    charlaSeguridad: false,
    equiposVerificados: false,
  });

  const hseItems = [
    { key: 'permisoTrabajo', label: 'Permiso de Trabajo firmado' },
    { key: 'ats', label: 'ATS (Análisis de Trabajo Seguro) completado' },
    { key: 'epp', label: 'EPP verificado para todo el personal' },
    { key: 'charlaSeguridad', label: 'Charla de seguridad realizada' },
    { key: 'equiposVerificados', label: 'Equipos y herramientas verificadas' },
  ];

  const allChecked = Object.values(hseChecklist).every(Boolean);

  const ordenesData = [
    { id: 1, numero: 'OT-2026-048', tipo: 'Perforación', locacion: 'Pozo XY-123', estado: 'En Ejecución', hseOk: true, prioridad: 'Alta' },
    { id: 2, numero: 'OT-2026-047', tipo: 'Workover', locacion: 'Pozo AB-456', estado: 'Pendiente HSE', hseOk: false, prioridad: 'Alta' },
    { id: 3, numero: 'OT-2026-046', tipo: 'Mantenimiento', locacion: 'Pozo MZ-789', estado: 'En Ejecución', hseOk: true, prioridad: 'Normal' },
    { id: 4, numero: 'OT-2026-045', tipo: 'Inspección', locacion: 'Batería Norte', estado: 'Completada', hseOk: true, prioridad: 'Normal' },
  ];

  const workflowSteps = ['Creación', 'HSE Check', 'Aprobación', 'Ejecución', 'Cierre'];

  const getActiveStep = (estado) => {
    switch (estado) {
      case 'Pendiente HSE': return 1;
      case 'Pendiente Aprobación': return 2;
      case 'En Ejecución': return 3;
      case 'Completada': return 5;
      default: return 0;
    }
  };

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Órdenes de Trabajo
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de OT con checklist HSE obligatorio
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nueva OT
        </Button>
      </Box>

      {/* Alerta HSE */}
      <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
        <Typography variant="body2">
          <strong>Recordatorio HSE:</strong> Ninguna Orden de Trabajo puede iniciar sin el checklist de seguridad completado al 100%.
        </Typography>
      </Alert>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="primary">23</Typography>
              <Typography variant="caption" color="text.secondary">Total OT</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="info.main">8</Typography>
              <Typography variant="caption" color="text.secondary">En Ejecución</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="warning.main">3</Typography>
              <Typography variant="caption" color="text.secondary">Pend. HSE</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="success.main">12</Typography>
              <Typography variant="caption" color="text.secondary">Completadas</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" fontWeight={700} color="error.main">2</Typography>
              <Typography variant="caption" color="text.secondary">Urgentes</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Lista de OT */}
      <Grid container spacing={3}>
        {ordenesData.map((orden) => (
          <Grid item xs={12} md={6} key={orden.id}>
            <Card
              sx={{
                border: orden.prioridad === 'Alta' ? `2px solid ${theme.palette.error.main}` : 'none',
                borderLeft: !orden.hseOk ? `4px solid ${theme.palette.warning.main}` : `4px solid ${theme.palette.success.main}`,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" fontWeight={600}>{orden.numero}</Typography>
                      {orden.prioridad === 'Alta' && <Chip label="URGENTE" size="small" color="error" />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">{orden.tipo} - {orden.locacion}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {orden.hseOk ? (
                      <Chip icon={<CheckIcon />} label="HSE OK" size="small" color="success" />
                    ) : (
                      <Chip icon={<WarningIcon />} label="HSE Pendiente" size="small" color="warning" />
                    )}
                  </Box>
                </Box>

                <Stepper activeStep={getActiveStep(orden.estado)} alternativeLabel sx={{ mb: 2 }}>
                  {workflowSteps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    label={orden.estado}
                    size="small"
                    color={orden.estado === 'Completada' ? 'success' : orden.estado === 'En Ejecución' ? 'info' : 'warning'}
                  />
                  <Box>
                    <IconButton size="small"><ViewIcon /></IconButton>
                    {!orden.hseOk && (
                      <Button size="small" variant="contained" color="warning" sx={{ ml: 1 }}>
                        Completar HSE
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog Nueva OT */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nueva Orden de Trabajo</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Trabajo</InputLabel>
                <Select label="Tipo de Trabajo">
                  <MenuItem value="perforacion">Perforación</MenuItem>
                  <MenuItem value="workover">Workover</MenuItem>
                  <MenuItem value="mantenimiento">Mantenimiento</MenuItem>
                  <MenuItem value="inspeccion">Inspección</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Locación</InputLabel>
                <Select label="Locación">
                  <MenuItem value="1">Pozo XY-123</MenuItem>
                  <MenuItem value="2">Pozo AB-456</MenuItem>
                  <MenuItem value="3">Pozo MZ-789</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción del Trabajo" multiline rows={2} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Prioridad</InputLabel>
                <Select label="Prioridad">
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                  <MenuItem value="urgente">Urgente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha Programada" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>

            {/* Checklist HSE */}
            <Grid item xs={12}>
              <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Checklist HSE Obligatorio
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Todos los ítems deben completarse antes de iniciar la OT
                  </Typography>
                  {hseItems.map((item) => (
                    <FormControlLabel
                      key={item.key}
                      control={
                        <Checkbox
                          checked={hseChecklist[item.key]}
                          onChange={(e) => setHseChecklist({ ...hseChecklist, [item.key]: e.target.checked })}
                          color="success"
                        />
                      }
                      label={item.label}
                      sx={{ display: 'block', mb: 1 }}
                    />
                  ))}
                  {allChecked && (
                    <Chip icon={<CheckIcon />} label="Checklist Completo" color="success" sx={{ mt: 1 }} />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)} disabled={!allChecked}>
            Crear OT
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdenesTrabajo;
